import AppLink from '@/components/elementary/AppLink'
import { Tag } from '@/components/elementary/Tag'
import { ArrowRight } from '@/components/icons/ArrowRight'
import { BasicImage, type BasicImageProps } from '@/components/page-builder/components/BasicImage'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import { cn } from '@/lib/styles'
import { LIBRARY_COMPONENT_TEST_IDS } from '@/lib/test-ids'
import type { GridCardProps } from '@/types/general'
import styles from './LibraryItem.module.css'

export const LibraryItem = ({
	loading,
	fetchPriority,
	entryItem,
}: {
	entryItem: GridCardProps
	loading?: BasicImageProps['loading']
	fetchPriority?: BasicImageProps['fetchPriority']
}) => {
	const { tag, image, categories, title, link } = entryItem
	return (
		<AppLink
			href={entryItem.link?.href ?? '#'}
			openExternalInNewTab={entryItem.link?.newTab ?? true}
			data-test-id={LIBRARY_COMPONENT_TEST_IDS.LINK}
			className="h-full items-baseline"
		>
			<div className="group/library-item flex flex-col gap-6 size-full">
				{image?.media?.url && (
					<div className="relative overflow-hidden rounded-xl w-full xl:max-w-75 aspect-3/2">
						<BasicImage
							fill
							component={entryItem.image}
							className="object-cover"
							sizes="(min-width: 1280px) 300px, (min-width: 640px) 450px, 100vw"
							fetchPriority={fetchPriority}
							loading={loading}
						/>
					</div>
				)}

				<div className="flex flex-col gap-3 flex-1">
					<div className="flex items-center gap-2">
						{tag && (
							<Tag
								name={tag.name}
								variant={tag.variant?.variant}
								size="small"
								customColors={
									tag.customColors
										? {
												backgroundColor: tag.customColors.backgroundColor?.color,
												textColor: tag.customColors.textColor?.color,
												borderColor: tag.customColors.borderColor?.color,
											}
										: undefined
								}
								className="w-fit"
							/>
						)}

						{categories?.map((category) => (
							<Paragraph
								key={category.documentId}
								className="text-neutral-500"
								fontSize="bodyRegularMedium"
							>
								{category?.title}
							</Paragraph>
						))}
					</div>

					<Heading tag="h3" className={cn('line-clamp-3', styles['library-title'])}>
						{title}
					</Heading>

					<div className="flex items-center gap-1 mt-auto">
						<Paragraph
							fontSize="bodyRegularMedium"
							className="underline font-bold group-hover/library-item:text-peach-600 group-focus-visible/library-item:text-peach-600 group-active/library-item:text-peach-600 transition-colors ease-in-out duration-200 text-inherit"
						>
							{link?.label}
						</Paragraph>
						<ArrowRight className="size-4 group-hover/library-item:text-peach-600 group-focus-visible/library-item:text-peach-600 group-active/library-item:text-peach-600 ml-0 group-hover/library-item:ml-1 transition-[margin] duration-200" />
					</div>
				</div>
			</div>
		</AppLink>
	)
}
